/**
 * Motor comun de los campos numericos.
 *
 * Un `<input type="number">` controlado directamente por el valor del formulario
 * es inusable: al escribir "1." o "-" el valor intermedio no es un numero, React
 * lo borra y el cursor salta. Por eso el input mantiene su propio texto y solo
 * emite hacia arriba cuando lo que hay se puede interpretar.
 *
 * Ademas, lo que se VE en modo edicion esta localizado a es-AR igual que la
 * celda de solo lectura: el input muestra "1.150.000,00" (separador de miles con
 * punto, coma decimal, sin cola de ceros) mientras que a la API viaja el numero
 * crudo (`1150000`). Sin esto, editar un renglon mostraba "1150000.0000" y se
 * sentia una planilla yanqui, no un ERP argentino.
 *
 * El truco para que editar sea comodo: cuando el campo toma foco se
 * DESAGRUPA —se muestra "1150000" o "1150000,5", facil de seleccionar y
 * corregir— y al salir se vuelve a agrupar. Es el mismo comportamiento de Odoo.
 *
 * Como se lee lo que se tipea —coma decimal, punto decimal o punto de miles—
 * NO se decide aca: es `parse-numero.ts`, que es una funcion pura y esta
 * documentada aparte porque es la que decide cuanta plata dice un campo.
 */

import { useEffect, useRef, useState } from 'react';

import { LOCALE } from '@/components/fields/format';
import { parsearNumero } from '@/components/fields/parse-numero';
import type { FieldMeta, FieldValue } from '@/lib/types';

/** Decimales que declara la metadata (el segundo elemento de `digits`). */
export function fraccionDe(meta: FieldMeta): number {
  const digits = meta.digits;
  if (Array.isArray(digits) && typeof digits[1] === 'number') return digits[1];
  return meta.type === 'integer' ? 0 : 2;
}

/**
 * @param entero `true` descarta la parte decimal.
 * @param decimal `true` emite string (los `DecimalField` viajan como string para
 *   no perder precision; convertirlos a `number` seria un error de redondeo en
 *   un balance).
 * @param minFraccion decimales minimos al MOSTRAR agrupado (un importe muestra
 *   siempre "…,00"; un `float` no fuerza ceros).
 * @param maxFraccion decimales maximos al mostrar (sobrantes se truncan: un
 *   `float` con precision 6 pero valor entero se ve "3", no "3,000000").
 */
export interface OpcionesNumericas {
  entero: boolean;
  decimal: boolean;
  minFraccion: number;
  maxFraccion: number;
  /** Agrupar de a miles al mostrar (por defecto, si). */
  agrupar?: boolean;
}

/** Valor del formulario a numero, o `null` si no es interpretable. */
function aNumero(valor: FieldValue): number | null {
  if (typeof valor === 'number') return Number.isFinite(valor) ? valor : null;
  if (typeof valor === 'string' && valor.trim() !== '') {
    const numero = Number(valor.trim());
    return Number.isFinite(numero) ? numero : null;
  }
  return null;
}

/** Forma "de reposo" del input: agrupada y en es-AR. `1150000` -> `1.150.000,00`. */
function formatearVisible(valor: FieldValue, opciones: OpcionesNumericas): string {
  const numero = aNumero(valor);
  if (numero === null) return '';
  return new Intl.NumberFormat(LOCALE, {
    minimumFractionDigits: opciones.minFraccion,
    maximumFractionDigits: Math.max(opciones.minFraccion, opciones.maxFraccion),
    useGrouping: opciones.agrupar ?? true,
  }).format(numero);
}

/**
 * Forma "de edicion": sin agrupar y sin cola de ceros, con coma decimal.
 * `1150000` -> `1150000`, `1150000.5` -> `1150000,5`. Es lo que se muestra
 * mientras el campo tiene el foco, para que corregir un digito no obligue a
 * pelear con los puntos de miles.
 */
function formatearEditable(valor: FieldValue, opciones: OpcionesNumericas): string {
  const numero = aNumero(valor);
  if (numero === null) return '';
  return new Intl.NumberFormat(LOCALE, {
    minimumFractionDigits: 0,
    maximumFractionDigits: Math.max(opciones.minFraccion, opciones.maxFraccion, 0),
    useGrouping: false,
  }).format(numero);
}

export interface BufferNumerico {
  texto: string;
  /** Al tomar foco: desagrupa para editar comodo. */
  alEntrar: () => void;
  alEscribir: (texto: string) => void;
  /** Normaliza y reagrupa al salir del campo. */
  alSalir: () => void;
}

export function useNumericBuffer(
  valor: FieldValue,
  onChange: (valor: FieldValue) => void,
  opciones: OpcionesNumericas,
): BufferNumerico {
  const [texto, setTexto] = useState<string>(() => formatearVisible(valor, opciones));
  // Lo ultimo que mostramos a partir de un valor externo; sirve para no pisar lo
  // que se esta tipeando cuando el valor vuelve del formulario sin cambios.
  const ultimoVisible = useRef<string>(formatearVisible(valor, opciones));
  // `true` mientras el campo tiene foco: el valor externo no debe reformatear el
  // texto que el usuario esta editando.
  const editando = useRef<boolean>(false);

  // El valor puede cambiar desde afuera (recarga del registro, rollback de un
  // guardado optimista). Se resincroniza solo si no estamos editando y no es lo
  // que acabamos de mostrar.
  useEffect(() => {
    if (editando.current) return;
    const externo = formatearVisible(valor, opciones);
    if (externo !== ultimoVisible.current) {
      ultimoVisible.current = externo;
      setTexto(externo);
    }
    // Solo depende del valor; `opciones` es estable en la practica (deriva de la
    // metadata del campo, que no cambia en vida del control).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valor]);

  const emitir = (crudo: string): void => {
    const limpio = parsearNumero(crudo);
    if (limpio === '' || limpio === '-' || limpio === '.' || limpio === '-.') {
      ultimoVisible.current = '';
      onChange(null);
      return;
    }
    const numero = Number(limpio);
    if (!Number.isFinite(numero)) {
      // Lo tipeado no es un numero. ANTES esto era un `return` mudo: el
      // formulario se quedaba con el valor VIEJO y `alSalir` dejaba el campo en
      // blanco, asi que la pantalla mostraba vacio mientras se iba a guardar
      // 5.000,00. Pegar "$ 1.234,56", "(1.234,56)" o un importe con el guion
      // atras —todo lo que sale de una factura, del homebanking o de un Excel—
      // caia aca. Emitir null es lo que `alSalir` ya daba por hecho en su
      // comentario, y deja el campo vacio de verdad: la pantalla y lo que se
      // guarda vuelven a decir lo mismo.
      ultimoVisible.current = '';
      onChange(null);
      return;
    }
    const final = opciones.entero ? Math.trunc(numero) : numero;
    const emitido: FieldValue = opciones.decimal ? String(final) : final;
    ultimoVisible.current = formatearVisible(emitido, opciones);
    onChange(emitido);
  };

  return {
    texto,
    alEntrar: () => {
      editando.current = true;
      setTexto(formatearEditable(valor, opciones));
    },
    alEscribir: (nuevo: string) => {
      setTexto(nuevo);
      emitir(nuevo);
    },
    alSalir: () => {
      editando.current = false;
      const limpio = parsearNumero(texto);
      if (limpio === '' || !Number.isFinite(Number(limpio))) {
        // Nada interpretable: se deja el campo vacio (el valor ya se emitio como
        // null en `alEscribir`), no un texto a medio escribir.
        setTexto('');
        return;
      }
      const numero = opciones.entero ? Math.trunc(Number(limpio)) : Number(limpio);
      const emitido: FieldValue = opciones.decimal ? String(numero) : numero;
      const visible = formatearVisible(emitido, opciones);
      ultimoVisible.current = visible;
      setTexto(visible);
    },
  };
}

/** Paso del input segun los decimales declarados en la metadata. */
export function stepFor(meta: FieldMeta): string {
  if (meta.type === 'integer') return '1';
  const digits = meta.digits;
  if (Array.isArray(digits) && typeof digits[1] === 'number' && digits[1] > 0) {
    return `0.${'0'.repeat(digits[1] - 1)}1`;
  }
  return 'any';
}
